import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  FileText,
  Check,
  ChevronsUpDown,
  X,
  Calculator,
} from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  listPricingPolicies,
  createPricingPolicy,
  updatePricingPolicy,
  deletePricingPolicy,
} from "@/lib/api/pricing-policy";
import type { PricingPolicy } from "@/lib/api/pricing-policy";
import { listSimulators } from "@/lib/api/simulators";
import { getErrorMessage } from "@/lib/api/client";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/admin/pricing-policy")({
  component: AdminPricingPolicyPage,
});

const margeSchema = z.coerce
  .number({ invalid_type_error: "La marge doit être un nombre" })
  .min(0, "La marge doit être comprise entre 0 et 100")
  .max(100, "La marge doit être comprise entre 0 et 100")
  .nullable()
  .optional();

const policySchema = z.object({
  offre: z.string().min(1, "L'offre est requise"),
  marge_min: margeSchema,
  marge_max: margeSchema,
  derogation: z.string().min(1, "La dérogation est requise"),
  validation_dfi: z.string().optional(),
});

type PolicyForm = z.infer<typeof policySchema>;

// Une borne laissée vide veut dire « pas de borne ». `valueAsNumber` en faisait NaN dès que
// le champ vide prenait le focus, et le schéma le refusait sans qu'aucun message ne s'affiche.
const borneDeMarge = {
  setValueAs: (valeur: unknown) =>
    valeur === "" || valeur === null || valeur === undefined ? null : Number(valeur),
};

function formatMarge(min: number | null, max: number | null): string {
  if (min != null && max != null) {
    if (min === max) return `${min}%`;
    return `${min}% — ${max}%`;
  }
  if (min != null) return `≥ ${min}%`;
  if (max != null) return `≤ ${max}%`;
  return "—";
}

function SimulatorMultiSelect({
  selectedIds,
  onChange,
}: {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators", "all-for-policy"],
    queryFn: () => listSimulators({ page_size: 100, include_unpublished: true }),
  });

  const simulators = simulatorsData?.items ?? [];

  const simulatorMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const sim of simulators) {
      map.set(sim.id, sim.name);
    }
    return map;
  }, [simulators]);

  const toggleSimulator = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const removeSimulator = (id: number) => {
    onChange(selectedIds.filter((sid) => sid !== id));
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {selectedIds.length === 0
              ? "Sélectionner des simulateurs..."
              : `${selectedIds.length} simulateur${selectedIds.length > 1 ? "s" : ""} sélectionné${selectedIds.length > 1 ? "s" : ""}`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Rechercher un simulateur..." />
            <CommandList>
              <CommandEmpty>Aucun simulateur trouvé.</CommandEmpty>
              <CommandGroup>
                {simulators.map((sim) => (
                  <CommandItem
                    key={sim.id}
                    value={sim.name}
                    onSelect={() => toggleSimulator(sim.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedIds.includes(sim.id)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {sim.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1">
              {simulatorMap.get(id) ?? `#${id}`}
              <button
                type="button"
                onClick={() => removeSimulator(id)}
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminPricingPolicyPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PricingPolicy | null>(
    null
  );
  const [createSimulatorIds, setCreateSimulatorIds] = useState<number[]>([]);
  const [editSimulatorIds, setEditSimulatorIds] = useState<number[]>([]);

  const { data: policies, isLoading } = useQuery({
    queryKey: ["pricing-policies"],
    queryFn: listPricingPolicies,
  });

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators", "all-for-policy"],
    queryFn: () => listSimulators({ page_size: 100, include_unpublished: true }),
  });

  const simulatorMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const sim of simulatorsData?.items ?? []) {
      map.set(sim.id, sim.name);
    }
    return map;
  }, [simulatorsData]);

  const createForm = useForm<PolicyForm>({
    resolver: zodResolver(policySchema),
    defaultValues: {
      offre: "",
      marge_min: null,
      marge_max: null,
      derogation: "",
      validation_dfi: "",
    },
  });

  const editForm = useForm<PolicyForm>({
    resolver: zodResolver(policySchema),
  });

  const createMutation = useMutation({
    mutationFn: createPricingPolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pricing-policies"] });
      toast.success("Politique tarifaire créée");
      setCreateDialogOpen(false);
      createForm.reset();
      setCreateSimulatorIds([]);
    },
    onError: (error) =>
      toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
      simulatorIds,
    }: {
      id: number;
      data: PolicyForm;
      simulatorIds: number[];
    }) =>
      updatePricingPolicy(id, {
        offre: data.offre,
        marge_min: data.marge_min ?? null,
        marge_max: data.marge_max ?? null,
        derogation: data.derogation,
        validation_dfi: data.validation_dfi || null,
        simulator_ids: simulatorIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pricing-policies"] });
      toast.success("Politique tarifaire mise à jour");
      setEditDialogOpen(false);
    },
    onError: (error) =>
      toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: deletePricingPolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pricing-policies"] });
      toast.success("Politique tarifaire supprimée");
      setDeleteDialogOpen(false);
    },
    onError: (error) =>
      toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const handleEdit = (policy: PricingPolicy) => {
    setSelectedPolicy(policy);
    editForm.reset({
      offre: policy.offre,
      marge_min: policy.marge_min,
      marge_max: policy.marge_max,
      derogation: policy.derogation,
      validation_dfi: policy.validation_dfi || "",
    });
    setEditSimulatorIds(policy.simulator_ids ?? []);
    setEditDialogOpen(true);
  };

  const handleDelete = (policy: PricingPolicy) => {
    setSelectedPolicy(policy);
    setDeleteDialogOpen(true);
  };

  const MargeFields = ({
    form,
    prefix,
  }: {
    form: ReturnType<typeof useForm<PolicyForm>>;
    prefix: string;
  }) => (
    <div className="space-y-2">
      <Label>Taux de marge à appliquer</Label>
      <div className="flex items-center gap-3">
        <div className="flex-1 space-y-1">
          <Label
            htmlFor={`${prefix}-marge-min`}
            className="text-xs text-muted-foreground"
          >
            Marge min (%)
          </Label>
          <Input
            id={`${prefix}-marge-min`}
            type="number"
            step="0.1"
            min="0"
            max="100"
            placeholder="Ex: 15"
            {...form.register("marge_min", borneDeMarge)}
          />
          {form.formState.errors.marge_min && (
            <p className="text-sm text-destructive">
              {form.formState.errors.marge_min.message}
            </p>
          )}
        </div>
        <div className="flex-1 space-y-1">
          <Label
            htmlFor={`${prefix}-marge-max`}
            className="text-xs text-muted-foreground"
          >
            Marge max (%)
          </Label>
          <Input
            id={`${prefix}-marge-max`}
            type="number"
            step="0.1"
            min="0"
            max="100"
            placeholder="Ex: 20"
            {...form.register("marge_max", borneDeMarge)}
          />
          {form.formState.errors.marge_max && (
            <p className="text-sm text-destructive">
              {form.formState.errors.marge_max.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <Header title="Politique tarifaire" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          {/* Actions bar */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Gérez les règles de politique tarifaire appliquées aux offres.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Ajouter une offre
            </Button>
          </div>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Offre</TableHead>
                    <TableHead>Taux de marge à appliquer</TableHead>
                    <TableHead>Dérogation à la politique tarifaire</TableHead>
                    <TableHead>Validation DFI</TableHead>
                    <TableHead>Simulateurs liés</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    [...Array(4)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton className="h-4 w-40" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-28" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-64" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-8 w-8" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : policies?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-32 text-center">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <FileText className="h-10 w-10 opacity-50" />
                          <p>Aucune politique tarifaire configurée</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    policies?.map((policy, index) => (
                      <TableRow
                        key={policy.id}
                        className="border-b transition-colors hover:bg-muted/50"
                        style={{
                          animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                        }}
                      >
                        <TableCell className="font-medium">
                          {policy.offre}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">
                            {formatMarge(policy.marge_min, policy.marge_max)}
                          </span>
                        </TableCell>
                        <TableCell>{policy.derogation}</TableCell>
                        <TableCell>
                          {policy.validation_dfi || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {policy.simulator_ids?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {policy.simulator_ids.map((id) => (
                                <Link
                                  key={id}
                                  to="/simulators/$id"
                                  params={{ id: String(id) }}
                                  className="inline-flex"
                                >
                                  <Badge
                                    variant="secondary"
                                    className="gap-1 text-xs cursor-pointer hover:bg-accent transition-colors"
                                  >
                                    <Calculator className="h-3 w-3" />
                                    {simulatorMap.get(id) ?? `#${id}`}
                                  </Badge>
                                </Link>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleEdit(policy)}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(policy)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Ajouter une offre</DialogTitle>
              <DialogDescription>
                Ajoutez une nouvelle règle de politique tarifaire.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={createForm.handleSubmit((data) =>
                createMutation.mutate({
                  offre: data.offre,
                  marge_min: data.marge_min ?? null,
                  marge_max: data.marge_max ?? null,
                  derogation: data.derogation,
                  validation_dfi: data.validation_dfi || null,
                  simulator_ids: createSimulatorIds,
                })
              )}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="create-offre">Offre</Label>
                <Input
                  id="create-offre"
                  placeholder="Ex: Livraison de repas sénior"
                  {...createForm.register("offre")}
                />
                {createForm.formState.errors.offre && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.offre.message}
                  </p>
                )}
              </div>
              <MargeFields form={createForm} prefix="create" />
              <div className="space-y-2">
                <Label htmlFor="create-derogation">
                  Dérogation à la politique tarifaire
                </Label>
                <Textarea
                  id="create-derogation"
                  placeholder="Ex: En cas de marge < 15%, une demande de dérogation..."
                  {...createForm.register("derogation")}
                />
                {createForm.formState.errors.derogation && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.derogation.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-validation">
                  Validation DFI (optionnel)
                </Label>
                <Input
                  id="create-validation"
                  placeholder="Ex: Si CA > 500K€"
                  {...createForm.register("validation_dfi")}
                />
              </div>
              <div className="space-y-2">
                <Label>Simulateurs liés (optionnel)</Label>
                <SimulatorMultiSelect
                  selectedIds={createSimulatorIds}
                  onChange={setCreateSimulatorIds}
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Création..." : "Créer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l&apos;offre</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={editForm.handleSubmit((data) =>
                selectedPolicy &&
                updateMutation.mutate({
                  id: selectedPolicy.id,
                  data,
                  simulatorIds: editSimulatorIds,
                })
              )}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="edit-offre">Offre</Label>
                <Input id="edit-offre" {...editForm.register("offre")} />
                {editForm.formState.errors.offre && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.offre.message}
                  </p>
                )}
              </div>
              <MargeFields form={editForm} prefix="edit" />
              <div className="space-y-2">
                <Label htmlFor="edit-derogation">
                  Dérogation à la politique tarifaire
                </Label>
                <Textarea
                  id="edit-derogation"
                  {...editForm.register("derogation")}
                />
                {editForm.formState.errors.derogation && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.derogation.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-validation">
                  Validation DFI (optionnel)
                </Label>
                <Input
                  id="edit-validation"
                  {...editForm.register("validation_dfi")}
                />
              </div>
              <div className="space-y-2">
                <Label>Simulateurs liés (optionnel)</Label>
                <SimulatorMultiSelect
                  selectedIds={editSimulatorIds}
                  onChange={setEditSimulatorIds}
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending
                    ? "Enregistrement..."
                    : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette offre</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer la politique tarifaire pour
                &laquo;&nbsp;{selectedPolicy?.offre}&nbsp;&raquo; ? Cette action
                est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  selectedPolicy && deleteMutation.mutate(selectedPolicy.id)
                }
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminGuard>
  );
}
